import {
  IActionReference,
  IAdjudicatorFeedback,
  IAvailableWorkflowActions,
  ICopyrightForm,
  IAuthorICMJEForm,
  IAuthorICMJEFormList,
  IDeadlineDurationConfig,
  IRevisionRound,
  IRoleType,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { getMissingFieldFieldsResponse } from '@publishing-engine/domain/utils';
import { IPeerReviewListItem } from '@publishing-engine/domain/views/review';
import {
  IGetReviewsSummaryResponse,
  IGetRevisionHistoryResponse,
  IGetSubmissionChangeRequestResponse,
  IGetSubmissionFieldsResponse,
  IGetSubmissionRejectionResponse,
  IGetSubmissionUnsubmitResponse,
  IGetSubmissionWithdrawalResponse,
  IGetSubmissionWorkflowResponse,
  IGetTransferOfferResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchSubmissionFields,
  fetchSubmissionWorkflow,
  fetchActiveRevision,
  fetchRevisionRoundHistory,
  fetchTransferOffer,
  fetchSubmissionRejection,
  fetchReviewsSummary,
  fetchSubmissionChangeRequest,
  fetchSubmissionWithdrawal,
  fetchRequestedChangeFields,
  fetchSubmissionUnsubmit,
  fetchCopyrightForm,
  fetchICMJEForm,
  fetchAdjudicatorReviewsSummary,
  logSubmissionAceessed,
  fetchICMJEFormList,
  fetchSubmissionContext
} from 'src/api/queries';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';
import useAuthAccessQuery from '../hooks/useAuthAccessQuery';

export interface InvalidateSubmissionQueryCallback {
  (): void;
  including: (additionalQueryKey: string) => InvalidateSubmissionQueryCallback;
  additionalQueryKeys: Set<string>;
}

export interface ISubmissionDataContext {
  workflowResponse: IGetSubmissionWorkflowResponse;
  availableActions: IAvailableWorkflowActions;
  availableReviewActions: IAvailableReviewActions;
  fieldsResponse: IGetSubmissionFieldsResponse;
  refetchSubmission: InvalidateSubmissionQueryCallback;
  copyrightForm?: ICopyrightForm;
  icmjeForm?: IAuthorICMJEForm[];
  icmjeFormList?: IAuthorICMJEFormList[];
  getDeadlinesForReviewState: (
    state: string
  ) => IDeadlineDurationConfig | undefined;
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
  activeRevision?: IRevisionRound;
  revisionHistory?: IGetRevisionHistoryResponse;
  isRevisionRound: boolean;
  transferOffer?: IGetTransferOfferResponse;
  missingFieldsResponse?: {
    fields: ISubmissionFieldsResponse;
    hasMissingFields: boolean;
  };
  rejection: IGetSubmissionRejectionResponse;
  reviewsSummary?: IGetReviewsSummaryResponse;
  changeRequestData?: IGetSubmissionChangeRequestResponse;
  withdrawal?: IGetSubmissionWithdrawalResponse;
  unsubmit?: IGetSubmissionUnsubmitResponse;
  isTaskCompleter?: boolean;
  isDecisionMaker?: boolean;
  adjudicatorReviewsSummary?: IAdjudicatorFeedback[];
  submissionContext: ISubmissionContext;
}

export const SubmissionDataContext = createContext<
  ISubmissionDataContext | undefined
>(undefined);

function getAvailableWorkflowActions(
  workflowResponse: IGetSubmissionWorkflowResponse,
  roleContext: string
): IAvailableWorkflowActions {
  const currentState = workflowResponse.workflowData.currentState;
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.submission.states[
      currentState
    ]?.actions || [];

  return {
    contains: function (
      actionReference: IActionReference['name'],
      withRole = false
    ) {
      return withRole
        ? actions.some(
            a => a.name === actionReference && a.roleType === roleContext
          )
        : actions.some(a => a.name === actionReference);
    }
  };
}

type IAvailableReviewActions = {
  contains: (
    review: IPeerReviewListItem,
    actionReference: IActionReference['name']
  ) => boolean;
};

function getAvailablReviewActions(
  workflowResponse: IGetSubmissionWorkflowResponse
): IAvailableReviewActions {
  const reviewTransitionGraph =
    workflowResponse.articleWorkflow.transitionGraph.review;

  return {
    contains: function (
      review: IPeerReviewListItem,
      actionReference: IActionReference['name']
    ) {
      const currentState = review.workflow.currentState;
      const actions = reviewTransitionGraph.states[currentState]?.actions || [];

      return actions.some(a => a.name === actionReference);
    }
  };
}

function getDeadlinesForReviewState(
  workflowResponse: IGetSubmissionWorkflowResponse,
  state: string
): IDeadlineDurationConfig | undefined {
  const durations =
    workflowResponse.articleWorkflow.transitionGraph.review.states[state]
      ?.deadlines?.durations;
  return durations;
}

export const useSubmissionData = (): ISubmissionDataContext => {
  const context = useContext(SubmissionDataContext);
  if (!context) {
    throw new Error(
      'useSubmissionData must be used within a SubmissionDataProvider'
    );
  }
  return context;
};

const SubmissionDataProvider = ({ children }: { children: JSX.Element }) => {
  const { roleContext, submissionId } = getSubmissionRequestContextFromRoute();
  const queryClient = useQueryClient();

  const workflowQueryKey = ['submission-workflow', submissionId];
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    isError: isErrorWorkflow,
    error: errorWorkflow
  } = useQuery({
    queryKey: workflowQueryKey,
    queryFn: () =>
      fetchSubmissionWorkflow(submissionId, roleContext as IRoleType),
    enabled: !!submissionId,
    retry: 0
  });

  const submissionAccessedQueryKey = ['submission-accessed', submissionId];
  useQuery({
    queryKey: submissionAccessedQueryKey,
    queryFn: () => logSubmissionAceessed(submissionId),
    enabled: !!submissionId && roleContext === 'editorial',
    retry: 0
  });

  const fieldsQueryKey = ['submission-fields', submissionId];
  const {
    data: fieldsData,
    isLoading: isLoadingFields,
    isError: isErrorFields
  } = useQuery({
    queryKey: fieldsQueryKey,
    queryFn: () =>
      fetchSubmissionFields(submissionId, roleContext as IRoleType),
    enabled: !!submissionId,
    retry: 0
  });

  const activeRevisionQueryKey = [
    'active-revision',
    { submissionId, roleType: roleContext }
  ];
  const { data: activeRevisionData } = useQuery({
    queryKey: activeRevisionQueryKey,
    queryFn: () => fetchActiveRevision(submissionId, roleContext as IRoleType),
    enabled: !!submissionId,
    retry: 0
  });

  const revisionRoundHistoryQueryKey = [
    'revision-history',
    { submissionId, roleType: roleContext }
  ];

  const { data: revisionRoundHistoryData } = useQuery({
    queryKey: revisionRoundHistoryQueryKey,
    queryFn: () =>
      fetchRevisionRoundHistory(submissionId, roleContext as IRoleType),
    enabled: !!submissionId,
    retry: 0
  });

  const transferOfferQueryKey = [
    'transfer-offer',
    { submissionId, roleType: roleContext }
  ];
  const { data: transferOfferData } = useQuery({
    queryKey: transferOfferQueryKey,
    queryFn: () => fetchTransferOffer(submissionId, roleContext as IRoleType),
    enabled: !!submissionId,
    retry: 0
  });

  const rejectionQueryKey = [
    'submission-rejection',
    { submissionId, roleType: roleContext }
  ];
  const { data: rejectionData } = useQuery({
    queryKey: rejectionQueryKey,
    queryFn: () =>
      fetchSubmissionRejection(submissionId, roleContext as IRoleType),
    enabled: workflowData?.workflowData.currentState === 'rejected',
    retry: 0
  });

  const reviewsSummaryQueryKey = ['reviews-summary', submissionId];
  const { data: reviewsSummary } = useQuery({
    queryKey: reviewsSummaryQueryKey,
    queryFn: () => fetchReviewsSummary(submissionId)
  });

  const { data: adjudicatorReviewsSummary } = useQuery({
    queryKey: ['adjudicator-reviews-summary', submissionId],
    queryFn: () => fetchAdjudicatorReviewsSummary(submissionId)
  });

  const changeRequestQueryKey = [
    'change-requests',
    { submissionId, roleType: roleContext }
  ];
  const { data: changeRequestData } = useQuery({
    queryKey: changeRequestQueryKey,
    queryFn: () =>
      fetchSubmissionChangeRequest(submissionId, roleContext as IRoleType),
    enabled: ['preProduction'].includes(
      workflowData?.workflowData?.currentState || ''
    )
  });

  const isChangeRequested = workflowData?.workflowData.changeRequest?.requested;
  const requestedChangeFieldsQueryKey = [
    'requested-change-fields',
    submissionId
  ];
  let isRequestedChangeFieldsLoading: boolean = false;
  const {
    data: requestedChangeFields,
    isLoading: isRequestedChangeFieldsRequestLoading,
    fetchStatus // ref issue: https://github.com/TanStack/query/issues/3975
  } = useQuery({
    queryKey: requestedChangeFieldsQueryKey,
    queryFn: () =>
      fetchRequestedChangeFields(submissionId, roleContext as IRoleType),
    enabled: !!submissionId && isChangeRequested,
    retry: 0
  });
  isRequestedChangeFieldsLoading =
    fetchStatus !== 'idle' && isRequestedChangeFieldsRequestLoading;

  const withdrawalQueryKey = [
    'withdrawal',
    { submissionId, roleType: roleContext }
  ];
  const { data: withdrawal } = useQuery({
    queryKey: withdrawalQueryKey,
    queryFn: () =>
      fetchSubmissionWithdrawal(submissionId, roleContext as IRoleType),
    enabled:
      workflowData?.workflowData.currentState === 'withdrawn' ||
      !!workflowData?.workflowData.withdrawal?.requested
  });

  const unsubmitQueryKey = [
    'unsubmit',
    { submissionId, roleType: roleContext }
  ];
  const { data: unsubmit } = useQuery({
    queryKey: unsubmitQueryKey,
    queryFn: () =>
      fetchSubmissionUnsubmit(submissionId, roleContext as IRoleType),
    enabled: workflowData?.workflowData.currentState === 'unsubmitted'
  });

  const copyrightFormQueryKey = [
    'copyright-form',
    { submissionId, roleType: roleContext }
  ];
  const { data: copyrightForm, isLoading: isCopyrightFormLoading } = useQuery({
    queryKey: copyrightFormQueryKey,
    queryFn: () => fetchCopyrightForm(submissionId, roleContext as IRoleType)
  });

  const icmjeFormQueryKey = [
    'submitting-author-only-icmje-form',
    { submissionId, roleType: roleContext }
  ];
  const { data: icmjeForm, isLoading: isICMJEFormLoading } = useQuery({
    queryKey: icmjeFormQueryKey,
    queryFn: () => fetchICMJEForm(submissionId, roleContext as IRoleType)
  });

  const icmjeFormForAllQueryKey = [
    'submitting-author-icmje-form-list',
    { submissionId, roleType: roleContext }
  ];

  const { data: icmjeFormList, isLoading: isICMJEFormListLoading } = useQuery({
    queryKey: icmjeFormForAllQueryKey,
    queryFn: () => fetchICMJEFormList(submissionId, roleContext as IRoleType)
  });

  const { isAllowed: isTaskCompleter, isLoading: isLoadingTaskCompleterCheck } =
    useAuthAccessQuery({
      relation: 'task_completer',
      entityType: 'journal_phase',
      entityId: workflowData
        ? `${workflowData?.submissionContextId}_${workflowData?.workflowData.currentState}`
        : ''
    });

  const { data: submissionContext, isLoading: isLoadingContext } = useQuery({
    queryKey: ['submission-context', workflowData?.submissionContextId],
    queryFn: () =>
      workflowData?.submissionContextId
        ? fetchSubmissionContext(workflowData?.submissionContextId)
        : null
  });

  const { isAllowed: isDecisionMaker, isLoading: isLoadingDecisionMakerCheck } =
    useAuthAccessQuery({
      relation: 'decision_maker',
      entityType: 'journal_phase',
      entityId: workflowData
        ? `${workflowData?.submissionContextId}_${workflowData?.workflowData.currentState}`
        : ''
    });

  const taskCompleterAuthAccessQuery = [
    'auth-access',
    {
      relation: 'task_completer',
      entityId: `${workflowData?.submissionContextId}_${workflowData?.workflowData.currentState}`,
      entityType: 'journal_phase'
    }
  ];

  const decisionMakerAuthAccessQuery = [
    'auth-access',
    {
      relation: 'decision_maker',
      entityId: `${workflowData?.submissionContextId}_${workflowData?.workflowData.currentState}`,
      entityType: 'journal_phase'
    }
  ];

  // Handle all states before rendering children
  if (
    isLoadingFields ||
    isLoadingWorkflow ||
    isRequestedChangeFieldsLoading ||
    isCopyrightFormLoading ||
    isICMJEFormLoading ||
    isICMJEFormListLoading ||
    isLoadingTaskCompleterCheck ||
    isLoadingDecisionMakerCheck ||
    isLoadingContext
  )
    return <Loading />;

  if (
    isErrorFields ||
    !fieldsData ||
    isErrorWorkflow ||
    !workflowData ||
    !submissionContext
  )
    return isForbiddenError(errorWorkflow) ? <Forbidden /> : <NotFound />;

  const reviewsQueryKey = [
    'submission-context',
    workflowData?.submissionContextId,
    submissionId,
    'reviews'
  ];
  const reviewQueueQueryKey = [
    'submission-context',
    workflowData?.submissionContextId,
    submissionId,
    'reviewer-invitations'
  ];
  function refetchSubmission() {
    queryClient.invalidateQueries({ queryKey: workflowQueryKey });
    queryClient.invalidateQueries({ queryKey: fieldsQueryKey });
    queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
    queryClient.invalidateQueries({ queryKey: reviewQueueQueryKey });
    queryClient.invalidateQueries({ queryKey: activeRevisionQueryKey });
    queryClient.invalidateQueries({ queryKey: transferOfferQueryKey });
    queryClient.invalidateQueries({ queryKey: reviewsSummaryQueryKey });
    queryClient.invalidateQueries({ queryKey: changeRequestQueryKey });
    queryClient.invalidateQueries({ queryKey: withdrawalQueryKey });
    queryClient.invalidateQueries({ queryKey: copyrightFormQueryKey });
    queryClient.invalidateQueries({ queryKey: icmjeFormQueryKey });
    queryClient.invalidateQueries({ queryKey: icmjeFormForAllQueryKey });
    queryClient.invalidateQueries({ queryKey: taskCompleterAuthAccessQuery });
    queryClient.invalidateQueries({ queryKey: decisionMakerAuthAccessQuery });
    queryClient.invalidateQueries({ queryKey: revisionRoundHistoryQueryKey });
    queryClient.invalidateQueries({ queryKey: submissionAccessedQueryKey });
    queryClient.invalidateQueries({ queryKey: ['recommendation-summary'] });

    if (roleContext === 'author') {
      queryClient.invalidateQueries({
        queryKey: ['feedback', { submissionId }]
      });
    }

    refetchSubmission.additionalQueryKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  }
  refetchSubmission.additionalQueryKeys = new Set<string>();
  refetchSubmission.including = (queryKey: string) => {
    refetchSubmission.additionalQueryKeys.add(queryKey);
    return refetchSubmission;
  };

  const submissionContextId = workflowData?.submissionContextId;

  const missingFieldsResponse = getMissingFieldFieldsResponse(
    fieldsData.fields
  );

  const availableActions = getAvailableWorkflowActions(
    workflowData,
    roleContext
  );

  const fieldsResponse =
    availableActions.contains('AuthorCompleteChangeRequest#fields', true) ||
    availableActions.contains('AuthorCompleteChangeRequest#form', true)
      ? requestedChangeFields!
      : fieldsData;

  return (
    <SubmissionDataContext.Provider
      value={{
        workflowResponse: workflowData,
        availableActions,
        availableReviewActions: getAvailablReviewActions(workflowData),
        fieldsResponse,
        refetchSubmission,
        copyrightForm: copyrightForm,
        icmjeForm: icmjeForm?.authorICMJEForm,
        icmjeFormList: icmjeFormList,
        getDeadlinesForReviewState: (state: string) =>
          getDeadlinesForReviewState(workflowData, state),
        submissionId,
        submissionContextId,
        organizationId: fieldsData.organizationId,
        activeRevision: activeRevisionData,
        revisionHistory: revisionRoundHistoryData,
        isRevisionRound: !!activeRevisionData,
        transferOffer: transferOfferData,
        missingFieldsResponse,
        rejection: rejectionData,
        reviewsSummary,
        adjudicatorReviewsSummary,
        changeRequestData,
        withdrawal,
        unsubmit,
        isTaskCompleter,
        isDecisionMaker,
        submissionContext,
      }}
    >
      {children}
    </SubmissionDataContext.Provider>
  );
};

export function getSubmissionRequestContextFromRoute() {
  const { submissionId } = useParams();

  if (document.location.href.match('my-submissions')) {
    if (!submissionId) {
      throw new Error('Unable to retrieve submissionId from params');
    }

    return {
      roleContext: 'author' as IRoleType,
      submissionId: submissionId as string
    };
  }

  if (document.location.href.match('workspace')) {
    const searchParams = new URLSearchParams(location.search);
    const submissionIdQuery = searchParams.get('selectedItem');

    if (!(submissionIdQuery || submissionId)) {
      throw new Error('Unable to retrieve submissionId from params');
    }

    return {
      roleContext: 'editorial' as IRoleType,
      submissionId: (submissionIdQuery ?? submissionId) as string
    };
  }

  throw new Error('Unable to retrieve submissionId from params');
}

export default SubmissionDataProvider;
